import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import {
  resolveAnchorWorld,
  worldDirToLocal,
} from '../../three/core/dimension/AnchorResolver';
import {
  createAnnotation,
  createManualDimension,
  resolveDimension,
} from '../../three/core/dimension/DimensionEngine';
import { boxForAnchor } from '../../three/core/dimension/PartResolver';
import { projectToScreen } from '../../three/core/dimension/ProjectionEngine';
import { findSnap } from '../../three/core/dimension/SnapEngine';
import {
  addDimensions,
  openDimensionEditor,
  removeDimension,
  setDimensionMode,
  setManualChainCount,
  setManualPendingCount,
} from '../../store/reducers/dimension.reducer';

/** Bấm lại đúng điểm cuối (sai số m) thì coi là "kết thúc chuỗi". */
const SAME_POINT_M = 0.003;

/** Kéo chuột quá ngưỡng này (px) thì coi là xoay camera, không phải click đặt điểm. */
const CLICK_MOVE_TOLERANCE_PX = 5;
const PREVIEW_ID = '__preview__';

const SNAP_STYLE = {
  corner: { shape: 'square', color: '#49AC3E' },
  edge: { shape: 'diamond', color: '#49AC3E' },
  face: { shape: 'diamond', color: '#49AC3E' },
  center: { shape: 'circle', color: '#49AC3E' },
  onEdge: { shape: 'circle', color: '#49AC3E' },
  surface: { shape: 'circle', color: '#258FD2' },
};
const PENDING_COLOR = '#C62828';
/** Giữ kết quả snap tối đa (ms) khi con trỏ và camera đứng yên. */
const SNAP_CACHE_MS = 400;

const isTypingTarget = target =>
  ['INPUT', 'SELECT', 'TEXTAREA'].includes(target?.tagName) ||
  target?.isContentEditable;

/**
 * Điều khiển chế độ "Manual DIM": click hai điểm trên mô hình để tạo DIM.
 *
 * - Lắng nghe trên canvas 3D ở pha capture để chặn `click` (và mousedown/up trái)
 *   không tới các handler chọn/kéo module của designer khi đang đo.
 *   OrbitControls dùng pointer events nên vẫn xoay được camera bình thường.
 * - Hover: bắt dính vào điểm kỹ thuật (SnapEngine), vẽ marker + DIM xem trước.
 * - Esc: huỷ điểm đang chọn dở; Esc lần nữa thoát chế độ. Enter: kết thúc chuỗi.
 *
 * Điểm đã chọn dở được giữ ở dạng anchor (không giữ toạ độ world) nên bám theo
 * module nếu nó di chuyển trong lúc đang đo.
 */
export class ManualDimensionController {
  /**
   * @param {{sceneManager: object, store: object, getGroupIndex: () => Map,
   *          labels?: Record<string,string>}} deps
   */
  constructor({
    sceneManager,
    store,
    getGroupIndex,
    getPartIndex = () => null,
    labels = {},
  }) {
    this.sceneManager = sceneManager;
    this.store = store;
    this.getGroupIndex = getGroupIndex;
    this.getPartIndex = getPartIndex;
    this.labels = labels;
    this.raycaster = new THREE.Raycaster();
    this.dom = null;
    this.pendingAnchor = null;
    this.chain = null; // { id, axis, offsetDirWorld }
    // Các đoạn đã tạo trong chuỗi đang dở, để hoàn tác từng điểm
    this.chainDimIds = [];
    this.chainStarts = []; // anchor đầu của từng đoạn tương ứng
    this.lastCommandNonce = 0;
    this.unsubscribe = null;
    this.cursor = null; // toạ độ trong canvas (px)
    this.downPos = null;
    this.previousCursorStyle = '';
    this.snapCache = null; // { key, time, snap }

    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onSwallowMouse = this.onSwallowMouse.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyCapture = this.onKeyCapture.bind(this);
    this.onStoreChange = this.onStoreChange.bind(this);
  }

  get state() {
    return this.store.getState().dimension;
  }

  attach() {
    const dom = this.sceneManager.renderer?.domElement;
    if (!dom || this.dom) return;
    this.dom = dom;
    this.previousCursorStyle = dom.style.cursor;
    dom.style.cursor = 'crosshair';
    // Bấm để đặt điểm không được chọn module (tay nắm di chuyển sẽ đè lên điểm)
    this.sceneManager.dragController?.setSelectionSuspended?.(true);
    dom.addEventListener('pointermove', this.onPointerMove);
    dom.addEventListener('pointerleave', this.onPointerLeave);
    dom.addEventListener('pointerdown', this.onPointerDown, true);
    dom.addEventListener('mousedown', this.onSwallowMouse, true);
    dom.addEventListener('mouseup', this.onSwallowMouse, true);
    dom.addEventListener('click', this.onClick, true);
    dom.addEventListener('contextmenu', this.onContextMenu, true);
    window.addEventListener('keydown', this.onKeyDown);
    // Pha capture ở document: chạy TRƯỚC handler Backspace/Delete của designer
    document.addEventListener('keydown', this.onKeyCapture, true);
    // Nút "Hoàn tất" / "Hoàn tác điểm" trên toolbar gửi lệnh qua store
    this.lastCommandNonce = this.state.manual.command?.nonce ?? 0;
    this.unsubscribe = this.store.subscribe(this.onStoreChange);
  }

  detach() {
    const dom = this.dom;
    if (!dom) return;
    dom.style.cursor = this.previousCursorStyle;
    this.sceneManager.dragController?.setSelectionSuspended?.(false);
    dom.removeEventListener('pointermove', this.onPointerMove);
    dom.removeEventListener('pointerleave', this.onPointerLeave);
    dom.removeEventListener('pointerdown', this.onPointerDown, true);
    dom.removeEventListener('mousedown', this.onSwallowMouse, true);
    dom.removeEventListener('mouseup', this.onSwallowMouse, true);
    dom.removeEventListener('click', this.onClick, true);
    dom.removeEventListener('contextmenu', this.onContextMenu, true);
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keydown', this.onKeyCapture, true);
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.dom = null;
    this.cursor = null;
    this.cancelPending();
  }

  // ---- toạ độ ----------------------------------------------------------

  toCanvas(event) {
    const rect = this.dom.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  size() {
    return { width: this.dom.clientWidth, height: this.dom.clientHeight };
  }

  groups(index = this.getGroupIndex()) {
    return [...index].map(([instanceId, group]) => ({ instanceId, group }));
  }

  snapAt(x, y, index = this.getGroupIndex(), partIndex = this.getPartIndex()) {
    const { width, height } = this.size();
    return findSnap({
      x,
      y,
      camera: this.sceneManager.camera,
      width,
      height,
      groups: this.groups(index),
      parts: partIndex?.targets ?? [],
      raycaster: this.raycaster,
    });
  }

  // ---- sự kiện ----------------------------------------------------------

  onPointerMove(e) {
    this.cursor = this.toCanvas(e);
  }

  onPointerLeave() {
    this.cursor = null;
  }

  onPointerDown(e) {
    if (e.button === 0) this.downPos = { x: e.clientX, y: e.clientY };
  }

  /** Chặn mousedown/up trái để designer không chọn hay kéo module khi đang đo. */
  onSwallowMouse(e) {
    if (e.button === 0) e.stopPropagation();
  }

  onClick(e) {
    if (e.button !== 0) return;
    const moved = this.downPos
      ? Math.hypot(e.clientX - this.downPos.x, e.clientY - this.downPos.y)
      : 0;
    this.downPos = null;
    // Vừa kéo xoay camera: không phải click đặt điểm
    if (moved > CLICK_MOVE_TOLERANCE_PX) return;
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = this.toCanvas(e);
    this.place(this.snapAt(x, y));
  }

  onContextMenu(e) {
    if (!this.pendingAnchor) return;
    e.preventDefault();
    e.stopPropagation();
    this.cancelPending();
  }

  onKeyDown(e) {
    if (isTypingTarget(e.target)) return;
    if (e.key === 'Escape') {
      if (this.pendingAnchor) this.cancelPending();
      else this.store.dispatch(setDimensionMode('idle'));
    } else if (e.key === 'Enter' && this.pendingAnchor) {
      this.cancelPending();
    }
  }

  /**
   * Backspace và Ctrl/Cmd+Z = hoàn tác điểm vừa đặt. Designer dùng Backspace để
   * xoá module đang chọn, nên phải chặn ở pha capture, và chỉ khi thật sự có gì
   * để hoàn tác (không thì trả phím lại cho designer như cũ).
   */
  onKeyCapture(e) {
    if (isTypingTarget(e.target)) return;
    const isUndoKey =
      e.key === 'Backspace' ||
      ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z');
    if (!isUndoKey || !this.pendingAnchor) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.undo();
  }

  /** Lệnh từ nút trên toolbar (Hoàn tất / Hoàn tác điểm). */
  onStoreChange() {
    const command = this.state.manual.command;
    if (!command || command.nonce === this.lastCommandNonce) return;
    this.lastCommandNonce = command.nonce;
    if (command.name === 'finish') this.finishChain();
    else if (command.name === 'undo') this.undo();
  }

  // ---- logic tạo DIM -----------------------------------------------------

  cancelPending() {
    const had = this.pendingAnchor || this.chain;
    this.pendingAnchor = null;
    this.chain = null;
    this.chainDimIds = [];
    this.chainStarts = [];
    if (had && this.state.manual.pendingCount !== 0) {
      this.store.dispatch(setManualPendingCount(0));
    }
    if (this.state.manual.chainCount !== 0) {
      this.store.dispatch(setManualChainCount(0));
    }
  }

  /** Kết thúc chuỗi: giữ nguyên các DIM đã tạo, bỏ điểm đang dở. */
  finishChain() {
    this.cancelPending();
  }

  /**
   * Hoàn tác một điểm: đang có đoạn trong chuỗi thì xoá đoạn cuối và lùi về điểm
   * đầu của nó; chỉ còn điểm đầu thì bỏ điểm đó.
   * @returns {boolean} true nếu có gì để hoàn tác
   */
  undo() {
    if (this.chainDimIds.length) {
      const id = this.chainDimIds.pop();
      const start = this.chainStarts.pop();
      this.store.dispatch(removeDimension(id));
      this.pendingAnchor = start;
      this.store.dispatch(setManualChainCount(this.chainDimIds.length));
      if (!this.chainDimIds.length) this.chain = null; // chuỗi mới: chọn lại trục
      return true;
    }
    if (this.pendingAnchor) {
      this.cancelPending();
      return true;
    }
    return false;
  }

  /** Tham số tạo DIM cho đoạn kế tiếp, có tính đến chuỗi đang dở. */
  segmentOptions(getGroup, camera, preview = false, getPartMeshes) {
    const { axis, chain } = this.state.manual;
    const options = {
      getGroup,
      getPartMeshes,
      camera,
      axisMode: this.chain?.axis ?? axis,
    };
    if (this.chain) {
      options.chainId = this.chain.id;
      // Giữ cùng phía đẩy DIM với đoạn đầu để cả chuỗi thẳng hàng
      const first = getGroup(this.pendingAnchor.instanceId);
      if (first && options.axisMode !== 'aligned') {
        options.offsetDirLocal = worldDirToLocal(
          first,
          this.chain.offsetDirWorld
        );
      }
    } else if (chain && !preview) {
      options.chainId = `chain_${uuidv4()}`;
    }
    return options;
  }

  /** Chế độ ghi chú: một click tạo ghi chú gắn vào điểm đó rồi mở khung soạn. */
  placeNote(snap) {
    const note = createAnnotation({
      anchor: snap.anchor,
      text: this.labels.noteText ?? 'Note',
    });
    this.store.dispatch(addDimensions([note]));
    // Thoát công cụ (gỡ listener) rồi mở khung soạn chữ cho ghi chú vừa tạo
    this.store.dispatch(setDimensionMode('idle'));
    this.store.dispatch(openDimensionEditor(note.id));
  }

  place(snap) {
    if (!snap) return;
    if (this.state.mode === 'annotate') {
      this.placeNote(snap);
      return;
    }
    if (!this.pendingAnchor) {
      this.pendingAnchor = snap.anchor;
      this.store.dispatch(setManualPendingCount(1));
      return;
    }
    const index = this.getGroupIndex();
    const getGroup = id => index.get(id) ?? null;
    const partIndex = this.getPartIndex();
    const getPartMeshes = partIndex?.getPartMeshes;

    // Bấm lại đúng điểm vừa đặt: cách tự nhiên để kết thúc chuỗi (hai điểm trùng
    // nhau không tạo được DIM nên trước đây click này bị bỏ qua im lặng)
    const pendingGroup = getGroup(this.pendingAnchor.instanceId);
    const pendingWorld =
      pendingGroup &&
      resolveAnchorWorld(
        pendingGroup,
        boxForAnchor(pendingGroup, this.pendingAnchor, { getPartMeshes }),
        this.pendingAnchor
      );
    if (pendingWorld && pendingWorld.distanceTo(snap.world) < SAME_POINT_M) {
      if (this.chainDimIds.length) this.finishChain();
      return;
    }

    const startAnchor = this.pendingAnchor;
    const dim = createManualDimension({
      anchors: [this.pendingAnchor, snap.anchor],
      ...this.segmentOptions(
        getGroup,
        this.sceneManager.camera,
        false,
        getPartMeshes
      ),
    });
    if (!dim) return; // hai điểm trùng nhau
    this.store.dispatch(addDimensions([dim]));

    if (this.state.manual.chain) {
      const resolved = resolveDimension(dim, getGroup, { getPartMeshes });
      this.chain = {
        id: dim.chainId,
        axis: dim.axis === 'custom' ? 'aligned' : dim.axis,
        offsetDirWorld:
          resolved?.offsetDir.clone() ?? new THREE.Vector3(0, 1, 0),
      };
      this.pendingAnchor = snap.anchor; // điểm cuối thành điểm đầu đoạn kế
      this.chainDimIds.push(dim.id);
      this.chainStarts.push(startAnchor);
      this.store.dispatch(setManualChainCount(this.chainDimIds.length));
    } else {
      this.chain = null;
      this.pendingAnchor = null;
      this.store.dispatch(setManualPendingCount(0));
    }
  }

  // ---- mỗi frame -----------------------------------------------------------

  /**
   * Snap theo con trỏ, chỉ tính lại khi con trỏ / camera / khung nhìn / số module
   * đổi (hoặc sau SNAP_CACHE_MS phòng khi module bị di chuyển). Snap duyệt cạnh
   * mesh và raycast kiểm tra che khuất nên không nên chạy mỗi frame.
   */
  cachedSnap(camera, width, height, index, partIndex) {
    const key = [
      this.cursor.x,
      this.cursor.y,
      width,
      height,
      index.size,
      // Mặt đá / ốp tường bị tạo lại khi module đổi: đổi mesh thì tính lại
      (partIndex?.targets ?? [])
        .map(t => `${t.instanceId}${t.part}${t.meshes.map(m => m.uuid)}`)
        .join('|'),
      ...camera.matrixWorld.elements,
      ...camera.projectionMatrix.elements,
    ].join(',');
    const now = Date.now();
    const cache = this.snapCache;
    if (cache && cache.key === key && now - cache.time < SNAP_CACHE_MS) {
      return cache.snap;
    }
    const snap = findSnap({
      x: this.cursor.x,
      y: this.cursor.y,
      camera,
      width,
      height,
      groups: this.groups(index),
      parts: partIndex?.targets ?? [],
      raycaster: this.raycaster,
    });
    this.snapCache = { key, time: now, snap };
    return snap;
  }

  /**
   * Tính marker và DIM xem trước cho frame hiện tại.
   * @param {THREE.Camera} camera
   * @param {number} width
   * @param {number} height
   * @param {Map} index - instanceId -> group
   * @returns {{markers: object[], previewItem: object|null}}
   */
  frame(camera, width, height, index, partIndex = null) {
    const markers = [];
    let previewItem = null;
    const getGroup = id => index.get(id) ?? null;
    const getPartMeshes = partIndex?.getPartMeshes;

    let pendingWorld = null;
    if (this.pendingAnchor) {
      const group = getGroup(this.pendingAnchor.instanceId);
      if (!group) {
        // Module chứa điểm đầu đã bị xoá
        this.cancelPending();
      } else {
        const box = boxForAnchor(group, this.pendingAnchor, { getPartMeshes });
        pendingWorld = resolveAnchorWorld(group, box, this.pendingAnchor);
        if (pendingWorld) {
          const s = projectToScreen(pendingWorld, camera, width, height);
          if (s.visible) {
            markers.push({
              x: s.x,
              y: s.y,
              shape: 'circle',
              filled: true,
              color: PENDING_COLOR,
            });
          }
        }
      }
    }

    const snap = this.cursor
      ? this.cachedSnap(camera, width, height, index, partIndex)
      : null;
    if (snap) {
      const style = SNAP_STYLE[snap.kind];
      markers.push({
        x: snap.screen.x,
        y: snap.screen.y,
        shape: style.shape,
        color: style.color,
        label: this.labels[snap.kind],
      });
      if (this.pendingAnchor && pendingWorld) {
        const dim = createManualDimension({
          anchors: [this.pendingAnchor, snap.anchor],
          ...this.segmentOptions(getGroup, camera, true, getPartMeshes),
        });
        if (dim) previewItem = { ...dim, id: PREVIEW_ID, preview: true };
      }
    }
    return { markers, previewItem };
  }
}

export { PREVIEW_ID };
